import { useState } from 'react';
import { useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useLukkSporsmaal, useSporsmaal, useSvarPaaSporsmaal, type SourceStatus } from '../api/useHypoteseKilder.ts';
import type { Hypothesis } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Brikker, Flate, Fot, Radiogruppe, Tekstknapp, Varsel } from './deler.tsx';
import { fornavn, hvem, navnAv } from './lesning.ts';
import { H_TIL_DEG } from './rommet.ts';
import { delsetning, kortDato, kortDatoAv } from './setning.ts';
import { fraHypotese } from './skjema.ts';

/**
 * Kildeeierens svar (H3.3, konseptsiden W10; Kh15): ett utkast, to felt, én fylt knapp. Siden er
 * den ENESTE veien kildens eier leser en annens utkast — gjennom spørsmålet, og bare mens det er
 * åpent. Etiketten sier hvorfor hun ser det og hvor lenge; foten sier FØR hun sender at utkastet
 * forsvinner for henne etterpå (avvik 11: forklar før du ber).
 */

const VURDERING: Array<[SourceStatus, string]> = [
  ['verifisert', 'Ja'],
  ['uavklart_definisjon', 'Nei, den er uklar'],
  ['maa_bygges', 'Må bygges'],
];
const FORSLAG_MAKS = 140;

export function KildeSporsmaalSide({ id }: { id: string }) {
  const sp = useSporsmaal(id);
  const me = useMe();
  const naviger = useNavigate();
  const svar = useSvarPaaSporsmaal();
  const lukk = useLukkSporsmaal();
  const [forslag, setForslag] = useState('');
  const [vurdering, setVurdering] = useState<SourceStatus | undefined>(undefined);
  const tilbake = { navn: H_TIL_DEG.navn, til: H_TIL_DEG.sti };

  if (sp.isPending) {
    return (
      <Flate tittel="Spørsmål" tilbake={tilbake}>
        <div role="status" aria-busy="true" aria-label="Henter spørsmålet" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
          <span className="panel-skjelett-linje panel-skjelett-kort" />
        </div>
      </Flate>
    );
  }
  if (sp.isError) {
    return (
      <Flate tittel="Spørsmål" tilbake={tilbake}>
        {/* K2/Kh15: et spørsmål som ikke finnes, sier det samme som et som ikke er ditt å svare på. */}
        <Varsel tone="info" rolle="status">
          {erNettfeil(sp.error) ? 'Fikk ikke hentet spørsmålet. Sjekk nettet og prøv igjen.' : 'Fant ikke spørsmålet. Det kan være lukket, eller ikke ditt å svare på.'}
        </Varsel>
      </Flate>
    );
  }

  const q = sp.data;
  const kilde = q.source.name.toLocaleLowerCase('nb');
  const spoer = navnAv(q.askedBy);
  const svarer = q.answerer.id === me.data?.id;
  const overskrift = (
    <>
      <p className="hb-liten k-sp-spor">
        Spørsmål om {kilde} · fra {spoer} {kortDatoAv(q.askedAt)} · {q.source.owner.id === me.data?.id ? 'du eier kilden' : `du svarer for ${navnAv(q.source.owner)}`}
      </p>
      <h2 className="hb-tittel">
        {spoer} spør om {kilde}
      </h2>
      <div className="hb-kort">
        <div className="hb-kort-hode">
          <b>
            <Ikon navn="help-circle-outline" /> Spørsmålet
          </b>
          <span className="hb-liten">{kortDatoAv(q.askedAt)}</span>
        </div>
        <p className="hb-tekst">{q.question}</p>
      </div>
    </>
  );

  if (!q.draft || !svarer) {
    return (
      <Flate tittel="Spørsmål" tilbake={tilbake}>
        {overskrift}
        <Varsel tone="info" rolle="status">
          {q.status === 'besvart' ? 'Spørsmålet er besvart. Du ser ikke utkastet lenger.' : q.status === 'lukket' ? 'Spørsmålet er lukket. Du ser ikke utkastet lenger.' : `Spørsmålet venter på ${navnAv(q.answerer)}.`}
        </Varsel>
      </Flate>
    );
  }

  const utkast = q.draft;
  const skjema = fraHypotese({ ...(utkast.form as unknown as Hypothesis), metricSource: utkast.form.metricSourceId ? q.source : undefined });
  const deler = delsetning(skjema, { kildenavn: q.source.name });
  const t = forslag.trim();
  const grunn = !t && !vurdering ? 'Skriv et forslag, eller svar på om definisjonen er avklart.' : t.length > FORSLAG_MAKS ? `Forslaget kan ha høyst ${FORSLAG_MAKS} tegn.` : null;
  const feil = svar.error ?? lukk.error;
  const feiltekst = feil ? (erApiFeil(feil) && !erNettfeil(feil) && feil.code !== 'unknown' ? feil.brukertekst : 'Fikk ikke sendt. Prøv igjen. Det du skrev står.') : null;

  const send = () =>
    svar.mutate(
      { id: q.id, body: { ...(t ? { suggestion: t } : {}), ...(vurdering ? { sourceAssessment: vurdering } : {}) } },
      { onSuccess: () => void naviger(H_TIL_DEG.sti, { state: { kvittering: `Svaret er sendt til ${spoer}. Du ser ikke utkastet lenger.` } }) },
    );
  const lukkUten = () => lukk.mutate(q.id, { onSuccess: () => void naviger(H_TIL_DEG.sti, { state: { kvittering: `Spørsmålet er lukket. ${spoer} skriver kilden selv.` } }) });

  return (
    <Flate
      tittel="Spørsmål"
      tilbake={tilbake}
      fot={
        <Fot
          venstre={<Tekstknapp onClick={lukkUten}>Lukk uten forslag</Tekstknapp>}
          hoyre={<span className="hb-dempet k-sp-etterpaa">Etterpå ser du ikke utkastet.</span>}
          grunn={grunn}
          primar={{ label: 'Send forslaget', onClick: send, opptatt: svar.isPending, ikon: 'send-outline' }}
        />
      }
    >
      {overskrift}
      <section className="k-sp-utkast" aria-labelledby="k-sp-utkast-h">
        <p id="k-sp-utkast-h" className="h-etikett">
          Utkastet til {fornavn(q.askedBy)} — du ser det fordi du ble spurt, til det er sendt inn eller spørsmålet er lukket
        </p>
        {utkast.title ? <p className="hb-fet">{utkast.title}</p> : null}
        <Brikker deler={deler} />
        <p className="hb-liten">
          Lagret {kortDatoAv(utkast.savedAt)} · svar innen {kortDato(q.answerBy)}
        </p>
      </section>
      <div className="h-felt">
        <label htmlFor="k-sp-forslag" className="h-etikett">
          Ditt forslag til måltall
        </label>
        <textarea id="k-sp-forslag" className="h-input h-tekstfelt" rows={3} maxLength={FORSLAG_MAKS} value={forslag} onChange={(e) => setForslag(e.target.value)} aria-describedby="k-sp-forslag-hjelp" />
        <span id="k-sp-forslag-hjelp" className="hb-liten">
          Valgfritt. {hvem(q.askedBy, me.data?.id, 'du')} velger selv om forslaget skal brukes.
        </span>
      </div>
      <div className="h-felt">
        <span id="k-sp-vurdering-h" className="h-etikett">
          Er definisjonen avklart?
        </span>
        <Radiogruppe etikettId="k-sp-vurdering-h" valg={VURDERING} aktiv={vurdering} onVelg={setVurdering} />
      </div>
      {feiltekst ? <Varsel>{feiltekst}</Varsel> : null}
    </Flate>
  );
}
